import React, { useState, useEffect, useRef } from 'react';
import Downshift from 'downshift';
import ControllerBuilder from '@septima/septima-search/src/js/ControllerBuilder';
import yaml from 'js-yaml';
import './SeptimaSearch.css';
import {
  ISeptimaSearchResultItem,
  ISearchController,
  ISeptimaSearchSource,
} from './interfaces';

export default function SeptimaSearch(props: ISeptimaSearchProps) {
  const { limit = 10, onResult, config } = props;

  const [controller, setController] = useState<ISearchController | null>(null);
  const [items, setItems] = useState<Array<ISeptimaSearchResultItem>>([]);
  const [sources, setSources] = useState<Array<ISeptimaSearchSource>>([]);
  const [type, setType] = useState<any>(undefined);
  const [isOpen, setIsOpen] = useState(false);

  const ref = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function getController() {
    let configuration;
    if (config) {
      if (typeof config === 'string') {
        configuration = yaml.safeLoad(config);
      } else {
        configuration = config;
      }
    } else {
      const response = await fetch('/config.yml');
      const yml = await response.text();
      configuration = yaml.safeLoad(yml);
    }

    return await new ControllerBuilder().setOptions(configuration).build();
  }
  useEffect(() => {
    getController().then((searchController: ISearchController) => {
      setController(searchController);
      searchController.getSources().then(s => {
        setSources(s);
      });
    });
    return () => {
      setController(null);
    };
    // eslint-disable-next-line
  }, []);
  async function resetSearch() {
    if (!controller) return;

    if (ref && ref.current) {
      (ref.current as any).reset({
        type: Downshift.stateChangeTypes.keyDownEscape,
        selectedItem: null,
        inputValue: '',
      });
    }
    setIsOpen(false);
    setSources(await controller.getSources());
    setType(undefined);
    search('').then(data => setItems(data));
  }
  async function resetSource() {
    if (!controller) return;
    setSources(await controller.getSources());
    setType(undefined);
    search('').then(data => setItems(data));
  }
  async function search(
    queryString: string,
    typeAndSource = { sources, type }
  ) {
    if (controller === null) throw new Error('Controller is not assigned.');
    const query = await controller.fetchData(
      queryString,
      undefined,
      limit,
      undefined,
      typeAndSource.sources.length > 1
        ? typeAndSource.sources
        : typeAndSource.sources.length === 1
          ? typeAndSource.sources[0].source
          : undefined,
      typeAndSource.type
    );
    return query.getAllResults();
  }
  useEffect(() => {
    if (controller) {
      search('').then(data => setItems(data));
    }
    // eslint-disable-next-line
  }, [controller]);

  const itemToString = (item: ISeptimaSearchResultItem) => {
    return item ? (item.title ? item.title : item.source) : '';
  };
  const getSource = async (sourceName: string) => {
    if (!controller) return null;
    const sources = await controller.getSources();
    return sources.filter(s => s.source === sourceName);
  };
  const onSelect = (item: ISeptimaSearchResultItem) => {
    if (!item) return;
    const { newquery, data, geometry, type, source } = item;
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
    if (newquery) {
      const cursorPosition = item.newquery.indexOf('<select></select>') - 1;
      const searchText = item.newquery.replace('<select></select>', ' ');
      getSource(source).then(s => {
        setSources(s);
        setType(type);
        if (cursorPosition > -1 && inputRef && inputRef.current) {
          inputRef.current.selectionStart = cursorPosition;
          inputRef.current.selectionEnd = cursorPosition;
        }
        // setTarget(searcher && searcher.plural ? searcher.plural : title);
        search(searchText, { sources: s, type: type }).then(items => {
          setItems(items);
        });
      });
    } else if (!data && !geometry) {
      getSource(source).then(s => {
        setSources(s);
        setType(type);
        search('', { sources: source, type: type }).then(i => setItems(i));
      });
    } else {
      item.complete().then(i => {
        onResult(item);
      });
      setIsOpen(false);
      if (inputRef && inputRef.current) {
        inputRef.current.blur();
      }
    }
  };
  const stateReducer = (state, changes) => {
    // const { highlightedIndex, inputValue, isOpen, selectedItem } = state;
    switch (changes.type) {
      case Downshift.stateChangeTypes.clickItem:
      case Downshift.stateChangeTypes.keyDownEnter:
        return {
          ...changes,
          inputValue:
            changes.selectedItem.newquery !== null
              ? changes.selectedItem.newquery.replace('<select></select>', '')
              : changes.inputValue,
        };
      //   return changes;
      default:
        return changes;
    }
  };
  const onInputChange = e => {
    search(e.target.value).then(data => {
      setItems(data);
      // setIsOpen(true);
    });
  };
  const renderSource = () => {
    if (sources.length === 1) {
      return (
        <span
          style={{
            margin: '0 10',
            fontSize: 12,
            fontWeight: 600,
            color: '#fff',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            height: 18,
          }}
        >
          <span
            style={{
              padding: '4px 0 4px 8px',
              backgroundColor: '#384D63',
              opacity: 0.7,
              borderRadius: '4px 0 0 4px',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle'
            }}
            title={sources[0].types[0].plural}
          >
            {sources[0].types[0].plural}
          </span>
          <span
            style={{
              padding: '4px 0 4px 24px',
              cursor: 'pointer',
              backgroundImage:
                'url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48IURPQ1RZUEUgc3ZnIFBVQkxJQyAiLS8vVzNDLy9EVEQgU1ZHIDEuMS8vRU4iICJodHRwOi8vd3d3LnczLm9yZy9HcmFwaGljcy9TVkcvMS4xL0RURC9zdmcxMS5kdGQiPjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgdmVyc2lvbj0iMS4xIiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0id2hpdGUiPjxwYXRoIGQ9Ik0xOSw2LjQxTDE3LjU5LDVMMTIsMTAuNTlMNi40MSw1TDUsNi40MUwxMC41OSwxMkw1LDE3LjU5TDYuNDEsMTlMMTIsMTMuNDFMMTcuNTksMTlMMTksMTcuNTlMMTMuNDEsMTJMMTksNi40MVoiIC8+PC9zdmc+)',
              backgroundSize: 18,
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
              width: 24,
              backgroundColor: '#384D63',
              opacity: 0.7,
              borderRadius: '0 4px 4px 0',
              verticalAlign: 'middle'
            }}
            onClick={() => {
              resetSource();
              if (inputRef && inputRef.current) {
                inputRef.current.focus();
              }
            }}
          />
        </span>
      );
    }
    return null;
  };
  return (
    <Downshift
      itemToString={itemToString}
      defaultIsOpen={isOpen}
      onSelect={onSelect}
      stateReducer={stateReducer}
      onOuterClick={() => setIsOpen(false)}
      ref={ref}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
      }) => (
          <div className={'septima-search'}>
            <div
              style={{
                height: 55,
                width: '100%',
                position: 'relative',
                paddingLeft: 8,
                borderBottom: '3px solid rgb(158, 178, 199,0.5)',
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {renderSource()}
              <input
                ref={inputRef}
                {...getInputProps({
                  onChange: onInputChange,
                  onKeyDown: e => {
                    if (e.key === 'Escape') {
                      resetSearch();
                    }
                  },
                  onFocus: () => setIsOpen(true),
                })}
                className={'ssInput'}
                placeholder="Søg adresse eller lokalplan ..."
              // onChange={e => search(e.target.value)}
              />
              <div
                onClick={() => {
                  resetSearch();
                }}
                className={inputValue ? 'ssCloseButton' : 'ssSearchButton'}
              ></div>
              <ul
                {...getMenuProps()}
                style={{
                  paddingInlineStart: 0,
                  // border: '1px solid #eee',
                  margin: 0,
                  position: 'absolute',
                  top: 51,
                  left: -1,
                  right: -1,
                  maxHeight: '50vh',
                  overflowY: 'auto',
                  boxShadow: '0 2px 8px 0 rgba(38,63,87,0.16)'
                }}
              >
                {isOpen
                  ? items.map((item, index) => {
                    return (
                      <li
                        {...getItemProps({
                          key: index,
                          index,
                          item,
                          style: {
                            cursor: 'pointer',
                            listStyle: 'none',
                            borderTop: '1px solid #DAE3ED',
                            height: 50,
                            backgroundColor:
                              highlightedIndex === index ? '#F8FBFE' : 'white',
                            fontWeight:
                              selectedItem === item ? 'bold' : 600,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 4px'
                          },
                        })}
                      >
                        <img
                          style={{ margin: '0 8px', width: 18, height: 18, opacity: 0.4 }}
                          src={item.image}
                          alt="icon"
                        />
                        <div
                          style={{
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                          title={item.title ? item.title : ''}
                        >
                          {item.title ? item.title : ''}
                        </div>
                      </li>
                    );
                  })
                  : null}
              </ul>
            </div>
          </div>
        )}
    </Downshift>
  );
}

interface ISeptimaSearchProps {
  limit?: number;
  onResult: Function;
  config?: string | object;
}
